// Dashboard.jsx
import React, { useState, useEffect } from "react";
import CurrentUserContext from "../../contexts/CurrentUserContext";
import { normalize, isIpaCorrect, isTextCorrect } from "../../utils/helpers";

import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import "./Dashboard.css";

const Dashboard = ({ onLogout }) => {
  const currentUser = React.useContext(CurrentUserContext);
  const [userData, setUserData] = useState({
    fullName: "",
    email: "",
    role: "",
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Teacher specific state
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentAttempts, setStudentAttempts] = useState([]);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [teacherView, setTeacherView] = useState("students");
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);

  // Student specific state
  const [myAttempts, setMyAttempts] = useState([]);
  const [myBestScore, setMyBestScore] = useState(null);
  const [myStatistics, setMyStatistics] = useState(null);
  const [selectedMyAttempt, setSelectedMyAttempt] = useState(null);
  const [showMyAttemptDetails, setShowMyAttemptDetails] = useState(false);
  const [isLoadingMyResults, setIsLoadingMyResults] = useState(false);

  const navigate = useNavigate();

  // Helper function for text normalization
  // const normalizeText = (s) => {
  //   if (!s) return "";
  //   return String(s)
  //     .trim()
  //     .toLowerCase()
  //     .replace(/[^\w\s'-]/g, "")
  //     .replace(/\s+/g, " ")
  //     .trim();
  // };

  // Helper function for IPA comparison
  // const isIpaCorrect = (userAnswer, possibleAnswers) => {
  //   if (!userAnswer || !possibleAnswers) return false;

  //   const normalizeIpa = (s) => {
  //     if (!s || typeof s !== "string") return "";
  //     s.trim()
  //       .replace(/^\/+|\/+$/g, "")
  //       .replace(/[ˈˌ]/g, "")
  //       .replace(/:/g, "ː")
  //       .replace(/\s/g, "")
  //       .toLowerCase();
  //   };

  //   return possibleAnswers.some(
  //     (correct) => normalizeIpa(userAnswer) === normalizeIpa(correct),
  //   );
  // };

  useEffect(() => {
    api.initToken();
    fetchUserData();
  }, []);

  useEffect(() => {
    if (userData.role === "student") {
      fetchMyResults();
      fetchMyStatistics();
    } else if (userData.role === "teacher" || userData.role === "admin") {
      fetchStudents();
      fetchStatistics();
    }
  }, [userData.role]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 3000);
  };

  const fetchUserData = async () => {
    try {
      setIsLoading(true);
      const response = await api.getUserInfo();
      setUserData({
        fullName: response.fullName || response.name || "",
        email: response.email || "",
        role: response.role || "student",
      });
    } catch (err) {
      console.error("Error fetching user data:", err);
      if (err.message?.includes("401") || err.status === 401) {
        showMessage("error", "Session expired. Please login again.");
        setTimeout(() => navigate("/login"), 2000);
      } else {
        showMessage("error", "Failed to load profile data");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Student functions
  const fetchMyResults = async () => {
    setIsLoadingMyResults(true);
    try {
      const response = await api.getMyTestHistory();
      setMyAttempts(response.data.attempts);
      setMyBestScore(response.data.bestAttempt);
    } catch (error) {
      console.error("Error fetching my results:", error);
      showMessage("error", "Failed to load your test history");
    } finally {
      setIsLoadingMyResults(false);
    }
  };

  const fetchMyStatistics = async () => {
    try {
      const response = await api.getMyStatistics();
      setMyStatistics(response.data);
    } catch (error) {
      console.error("Error fetching my statistics:", error);
    }
  };

  const viewMyAttemptDetails = async (attemptId) => {
    try {
      const response = await api.getMyAttemptDetails(attemptId);
      setSelectedMyAttempt(response.data);
      setShowMyAttemptDetails(true);
    } catch (error) {
      console.error("Error fetching attempt details:", error);
      showMessage("error", "Failed to load attempt details");
    }
  };

  // Teacher functions
  const fetchStudents = async () => {
    setIsLoadingStudents(true);
    try {
      const response = await api.getAllStudentsResults();
      setStudents(response.data.students);
    } catch (error) {
      console.error("Error fetching students:", error);
      showMessage("error", "Failed to load students data");
    } finally {
      setIsLoadingStudents(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.getTestStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error("Error fetching statistics:", error);
    }
  };

  const viewStudentHistory = async (studentId) => {
    setIsLoadingStudents(true);
    try {
      const response = await api.getStudentTestHistory(studentId);
      setStudentAttempts(response.data.attempts);
      setSelectedStudent(response.data.student);
      setTeacherView("history");
    } catch (error) {
      console.error("Error fetching student history:", error);
      showMessage("error", "Failed to load student history");
    } finally {
      setIsLoadingStudents(false);
    }
  };

  const viewAttemptDetails = async (studentId, attemptId) => {
    try {
      const response = await api.getStudentAttemptDetails(studentId, attemptId);
      const attempt = response.data.attempt;
      setSelectedAttempt(attempt);
      setTeacherView("attemptDetails");
    } catch (error) {
      console.error("Error fetching attempt details:", error);
      showMessage("error", "Failed to load attempt details");
    }
  };

  const handleUserInfoChange = (e) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value,
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value,
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const updateData = {};
      if (userData.fullName) updateData.fullName = userData.fullName;
      if (userData.email) updateData.email = userData.email;

      await api.updateUserInfo(updateData);
      showMessage("success", "Profile updated successfully!");
      setIsEditing(false);
      await fetchUserData();
    } catch (err) {
      console.error("Error updating profile:", err);
      showMessage("error", err.message || "Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!passwordData.currentPassword) {
      showMessage("error", "Please enter current password");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      showMessage("error", "New password must be at least 6 characters");
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showMessage("error", "New passwords do not match");
      return;
    }

    setIsSaving(true);

    try {
      await api.updateUserInfo({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      showMessage("success", "Password changed successfully!");
      setIsChangingPassword(false);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      console.error("Error changing password:", err);
      showMessage("error", err.message || "Failed to change password");
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    onLogout();
    navigate("/login");
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsChangingPassword(false);
    fetchUserData();
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  // Render student results
  const renderStudentResults = () => {
    if (isLoadingMyResults) {
      return <div className='loading-spinner'>Loading your results...</div>;
    }

    if (myAttempts.length === 0) {
      return (
        <div className='no-results'>
          <p>You haven't taken any tests yet.</p>
          <p>Go to the Phonetics page to start your first test!</p>
        </div>
      );
    }

    return (
      <div className='student-results-section'>
        <div className='statistics-cards'>
          {myStatistics && (
            <>
              <div className='stat-card'>
                <h3>Total Attempts</h3>
                <p className='stat-number'>{myStatistics.totalAttempts}</p>
              </div>
              <div className='stat-card'>
                <h3>Best Score</h3>
                <p className='stat-number'>
                  {myBestScore
                    ? `${myBestScore.score}/${myBestScore.totalQuestions}`
                    : "N/A"}
                </p>
              </div>
              <div className='stat-card'>
                <h3>Average Score</h3>
                <p className='stat-number'>
                  {myStatistics.averageScore?.toFixed(1)}%
                </p>
              </div>
              <div className='stat-card'>
                <h3>Last 7 Days</h3>
                <p className='stat-number'>
                  {myStatistics.lastWeekAttempts} tests
                </p>
              </div>
            </>
          )}
        </div>

        <div className='attempts-table-container'>
          <h3>Your Test History</h3>
          <table className='attempts-table'>
            <thead>
              <tr>
                <th>Attempt #</th>
                <th>Variant</th>
                <th>Score</th>
                <th>Percentage</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {myAttempts.map((attempt) => (
                <tr key={attempt._id}>
                  <td>{attempt.attemptNumber}</td>
                  <td>{attempt.variant === "uk" ? "🇬🇧 UK" : "🇺🇸 US"}</td>
                  <td>
                    {attempt.score}/{attempt.totalQuestions}
                  </td>
                  <td
                    className={
                      attempt.percentage >= 70 ? "high-score" : "low-score"
                    }
                  >
                    {attempt.percentage.toFixed(1)}%
                  </td>
                  <td>{new Date(attempt.completedAt).toLocaleDateString()}</td>
                  <td>
                    <button
                      onClick={() => viewMyAttemptDetails(attempt._id)}
                      className='view-button'
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render student attempt details modal
  const renderStudentAttemptDetails = () => {
    if (!showMyAttemptDetails || !selectedMyAttempt) return null;

    const questions = selectedMyAttempt.questions || {};
    const answers = selectedMyAttempt.answers || {};

    const isAnswerCorrect = (question, userAnswer) => {
      if (!userAnswer) return false;

      if (question.type === "multiple-choice") {
        return userAnswer === question.correctAnswer;
      } else if (question.type === "ipa-input") {
        const normalizeIpa = (s) =>
          s
            .trim()
            .replace(/^\/+|\/+$/g, "")
            .replace(/[ˈˌ]/g, "")
            .replace(/:/g, "ː")
            .replace(/\s/g, "")
            .toLowerCase();
        return question.possibleAnswers?.some(
          (correct) => normalizeIpa(userAnswer) === normalizeIpa(correct),
        );
      } else {
        return normalize(userAnswer) === normalize(question.correctAnswer);
      }
    };

    return (
      <div className='attempt-details-modal'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h3>Attempt #{selectedMyAttempt.attemptNumber} Details</h3>
            <button
              onClick={() => setShowMyAttemptDetails(false)}
              className='close-button'
            >
              ✖
            </button>
          </div>

          <div className='attempt-summary'>
            <p>
              <strong>Variant:</strong>{" "}
              {selectedMyAttempt.variant === "uk" ? "🇬🇧 UK" : "🇺🇸 US"}
            </p>
            <p>
              <strong>Score:</strong> {selectedMyAttempt.score}/
              {selectedMyAttempt.totalQuestions}
            </p>
            <p>
              <strong>Percentage:</strong>{" "}
              {selectedMyAttempt.percentage.toFixed(1)}%
            </p>
            <p>
              <strong>Completed:</strong>{" "}
              {new Date(selectedMyAttempt.completedAt).toLocaleString()}
            </p>
          </div>

          <div className='answers-list'>
            <h4>Your Answers</h4>
            {Object.entries(questions).map(
              ([sectionName, sectionQuestions]) => (
                <div key={sectionName} className='section-answers'>
                  <h5>{sectionName.replace("section", "Section ")}</h5>
                  {sectionQuestions.map((q, idx) => {
                    const userAnswer = answers[q.id];
                    const isCorrect = isAnswerCorrect(q, userAnswer);

                    return (
                      <div
                        key={q.id}
                        className={`answer-item ${isCorrect ? "correct" : "incorrect"}`}
                      >
                        <div className='question-detail'>
                          <strong>Question {idx + 1}:</strong>
                          {q.word && <span> {q.word}</span>}
                          {q.transcription && <span> {q.transcription}</span>}
                        </div>
                        <div className='answer-detail'>
                          <div>
                            <strong>Your answer:</strong>{" "}
                            {userAnswer || "(not answered)"}
                          </div>
                          <div>
                            <strong>Correct answer:</strong> {q.correctAnswer}
                          </div>
                          <div>
                            <strong>Other possible answers:</strong>{" "}
                            {q.possibleAnswers
                              ? q.possibleAnswers
                                  .filter(
                                    (transcription) =>
                                      transcription !== q.correctAnswer,
                                  )
                                  .join(", ")
                              : " - "}
                          </div>
                          <div className='verdict'>
                            {isCorrect ? "✅ Correct" : "❌ Incorrect"}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ),
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render teacher dashboard
  const renderTeacherDashboard = () => {
    if (teacherView === "students") {
      return (
        <div className='teacher-section'>
          <div className='statistics-cards'>
            {statistics && (
              <>
                <div className='stat-card'>
                  <h3>Total Students</h3>
                  <p className='stat-number'>{statistics.totalStudents}</p>
                </div>
                <div className='stat-card'>
                  <h3>Total Attempts</h3>
                  <p className='stat-number'>{statistics.totalAttempts}</p>
                </div>
                <div className='stat-card'>
                  <h3>Average Score</h3>
                  <p className='stat-number'>
                    {statistics.averageScore?.toFixed(1)}%
                  </p>
                </div>
                <div className='stat-card'>
                  <h3>Active Students</h3>
                  <p className='stat-number'>
                    {statistics.studentsWithAttempts}
                  </p>
                </div>
              </>
            )}
          </div>

          <div className='students-table-container'>
            <div className='section-header'>
              <h3>All Students</h3>
              <button onClick={fetchStudents} className='refresh-button'>
                Refresh
              </button>
            </div>
            {isLoadingStudents ? (
              <div className='loading-spinner'>Loading...</div>
            ) : (
              <table className='students-table'>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Best Score</th>
                    <th>Latest Score</th>
                    <th>Attempts</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <tr key={student.id}>
                      <td>{student.fullName}</td>
                      <td
                        className={
                          student.bestAttempt?.percentage >= 70
                            ? "high-score"
                            : "low-score"
                        }
                      >
                        {student.bestAttempt
                          ? `${student.bestAttempt.score}/${student.bestAttempt.totalQuestions}`
                          : "No attempts"}
                      </td>
                      <td>
                        {student.latestAttempt
                          ? `${student.latestAttempt.score}/${student.latestAttempt.totalQuestions}`
                          : "No attempts"}
                      </td>
                      <td>{student.totalAttempts}</td>
                      <td>
                        <button
                          onClick={() => viewStudentHistory(student.id)}
                          className='view-button'
                        >
                          View History
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      );
    }

    if (teacherView === "history" && selectedStudent) {
      return (
        <div className='teacher-section'>
          <div className='section-header'>
            <button
              onClick={() => setTeacherView("students")}
              className='back-button'
            >
              ← Back to Students
            </button>
            <h3>{selectedStudent.fullName}'s Test History</h3>
          </div>

          <div className='student-stats'>
            <div className='stat-card small'>
              <h4>Total Attempts</h4>
              <p>{studentAttempts.length}</p>
            </div>
          </div>

          <div className='students-table-container'>
            <table className='students-table'>
              <thead>
                <tr>
                  <th>Attempt #</th>
                  <th>Variant</th>
                  <th>Score</th>
                  <th>Percentage</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {studentAttempts.map((attempt) => (
                  <tr key={attempt._id}>
                    <td>{attempt.attemptNumber}</td>
                    <td>{attempt.variant === "uk" ? "🇬🇧 UK" : "🇺🇸 US"}</td>
                    <td>
                      {attempt.score}/{attempt.totalQuestions}
                    </td>
                    <td
                      className={
                        attempt.percentage >= 70 ? "high-score" : "low-score"
                      }
                    >
                      {attempt.percentage.toFixed(1)}%
                    </td>
                    <td>
                      {new Date(attempt.completedAt).toLocaleDateString()}
                    </td>
                    <td>
                      <button
                        onClick={() =>
                          viewAttemptDetails(selectedStudent.id, attempt._id)
                        }
                        className='view-button'
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (teacherView === "attemptDetails" && selectedAttempt) {
      const questions = selectedAttempt.questions || {};
      const answers = selectedAttempt.answers || {};

      return (
        <div className='teacher-section'>
          <div className='section-header'>
            <button
              onClick={() => setTeacherView("history")}
              className='back-button'
            >
              ← Back to History
            </button>
            <h3>Attempt #{selectedAttempt.attemptNumber} Details</h3>
          </div>

          <div className='attempt-summary'>
            <p>
              <strong>Student:</strong> {selectedStudent?.fullName}
            </p>
            <p>
              <strong>Score:</strong> {selectedAttempt.score}/
              {selectedAttempt.totalQuestions}
            </p>
            <p>
              <strong>Percentage:</strong>{" "}
              {selectedAttempt.percentage.toFixed(1)}%
            </p>
            <p>
              <strong>Variant:</strong>{" "}
              {selectedAttempt.variant === "uk" ? "🇬🇧 UK" : "🇺🇸 US"}
            </p>
            <p>
              <strong>Completed:</strong>{" "}
              {new Date(selectedAttempt.completedAt).toLocaleString()}
            </p>
          </div>

          <div className='attempt-answers-list'>
            <h4>Detailed Answers</h4>
            {Object.entries(questions).map(
              ([sectionName, sectionQuestions]) => (
                <div key={sectionName} className='section-answers'>
                  <h5>{sectionName.replace("section", "Section ")}</h5>
                  {sectionQuestions.map((q, idx) => {
                    const userAnswer = answers[q.id];
                    const isCorrect =
                      q.type === "multiple-choice"
                        ? userAnswer === q.correctAnswer
                        : q.type === "ipa-input"
                          ? isIpaCorrect(userAnswer, q.possibleAnswers)
                          : normalize(userAnswer) ===
                            normalize(q.correctAnswer);

                    return (
                      <div
                        key={q.id}
                        className={`answer-item ${isCorrect ? "correct" : "incorrect"}`}
                      >
                        <div className='question-detail'>
                          <strong>Question {idx + 1}:</strong>
                          {q.word && <span> Word: {q.word}</span>}
                          {q.transcription && (
                            <span> Transcription: {q.transcription}</span>
                          )}
                        </div>
                        <div className='answer-detail'>
                          <div>
                            <strong>Student's answer:</strong>{" "}
                            {userAnswer || "(not answered)"}
                          </div>
                          <div>
                            <strong>Correct answer:</strong> {q.correctAnswer}
                          </div>
                          <div>
                            <strong>Other possible answers:</strong>{" "}
                            {q.possibleAnswers
                              ? q.possibleAnswers
                                  .filter(
                                    (transcription) =>
                                      transcription !== q.correctAnswer,
                                  )
                                  .join(", ")
                              : " - "}
                          </div>
                          <div className='verdict'>
                            {isCorrect ? "✅ Correct" : "❌ Incorrect"}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ),
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  if (isLoading) {
    return (
      <div className='dashboard-container'>
        <div className='loading-spinner'>
          <div className='spinner'></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <CurrentUserContext.Provider value={currentUser}>
      <div className='dashboard-container'>
        <div className='dashboard-card'>
          <div className='dashboard-header'>
            <div>
              <h1 className='dashboard-title'>Dashboard</h1>
              <p className='dashboard-subtitle'>
                {userData.role === "teacher" || userData.role === "admin"
                  ? "Manage students and view test results"
                  : "View your test results and manage your profile"}
              </p>
            </div>
            <button onClick={handleLogout} className='logout-button'>
              <svg
                width='18'
                height='18'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <path d='M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' />
                <polyline points='16 17 21 12 16 7' />
                <line x1='21' y1='12' x2='9' y2='12' />
              </svg>
              Logout
            </button>
          </div>

          {message.text && (
            <div className={`message ${message.type}`}>
              {message.type === "success" ? "✓" : "⚠"} {message.text}
            </div>
          )}

          {/* Teacher View */}
          {(userData.role === "teacher" || userData.role === "admin") &&
            renderTeacherDashboard()}

          {/* Student View */}
          {userData.role === "student" && (
            <>
              {renderStudentResults()}
              {renderStudentAttemptDetails()}
            </>
          )}

          {/* Profile Section (visible for all users) */}
          <div className='form-section'>
            <div className='section-header'>
              <h2 className='section-title'>Profile Information</h2>
              {!isEditing && !isChangingPassword && (
                <button
                  onClick={() => setIsEditing(true)}
                  className='edit-button'
                >
                  Edit Profile
                </button>
              )}
            </div>

            {isEditing ? (
              <form onSubmit={handleUpdateProfile} className='dashboard-form'>
                <div className='input-group'>
                  <input
                    type='text'
                    name='fullName'
                    placeholder='Full Name'
                    value={userData.fullName}
                    onChange={handleUserInfoChange}
                    className='auth-input'
                    required
                  />
                </div>

                <div className='input-group'>
                  <input
                    type='email'
                    name='email'
                    placeholder='Email'
                    value={userData.email}
                    onChange={handleUserInfoChange}
                    className='auth-input'
                    required
                  />
                </div>

                <div className='form-actions'>
                  <button
                    type='button'
                    onClick={handleCancel}
                    className='cancel-button'
                  >
                    Cancel
                  </button>
                  <button
                    type='submit'
                    disabled={isSaving}
                    className='save-button'
                  >
                    {isSaving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            ) : (
              <div className='user-info-display'>
                <div className='info-row'>
                  <span className='info-label'>Full Name:</span>
                  <span className='info-value'>
                    {userData.fullName || "Not set"}
                  </span>
                </div>
                <div className='info-row'>
                  <span className='info-label'>Email:</span>
                  <span className='info-value'>
                    {userData.email || "Not set"}
                  </span>
                </div>
                <div className='info-row'>
                  <span className='info-label'>Role:</span>
                  <span className='info-value'>
                    {userData.role || "Student"}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Password Change Section */}
          {/* <div className='form-section'>
            <div className='section-header'>
              <h2 className='section-title'>Security</h2>
              {!isChangingPassword && !isEditing && (
                <button
                  onClick={() => setIsChangingPassword(true)}
                  className='edit-button'
                >
                  Change Password
                </button>
              )}
            </div>

            {isChangingPassword && (
              <form onSubmit={handleChangePassword} className='dashboard-form'>
                <div className='input-group'>
                  <input
                    type='password'
                    name='currentPassword'
                    placeholder='Current Password'
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    className='auth-input'
                    required
                  />
                </div>

                <div className='input-group'>
                  <input
                    type='password'
                    name='newPassword'
                    placeholder='New Password (min. 6 characters)'
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className='auth-input'
                    required
                  />
                </div>

                <div className='input-group'>
                  <input
                    type='password'
                    name='confirmPassword'
                    placeholder='Confirm New Password'
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className='auth-input'
                    required
                  />
                </div>

                <div className='form-actions'>
                  <button
                    type='button'
                    onClick={handleCancel}
                    className='cancel-button'
                  >
                    Cancel
                  </button>
                  <button
                    type='submit'
                    disabled={isSaving}
                    className='save-button'
                  >
                    {isSaving ? "Changing..." : "Change Password"}
                  </button>
                </div>
              </form>
            )}
          </div> */}
        </div>
      </div>
    </CurrentUserContext.Provider>
  );
};

export default Dashboard;
